import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { MAX_ALLOWED_WORK_TYPES, type AllowedWorkTypes, type ServiceType } from '../types';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface AllowedWorkTypesSectionProps {
  employeeId: string;
  /** The work types the company can see (active ones are offered). */
  serviceTypes: ServiceType[];
  /** False while the work types are still loading or could not be read: the server needs them to verify a change, so the list cannot be edited then. */
  serviceTypesReady: boolean;
  /** Whether the person may change the list (the server decides; this only decides what is offered). */
  canEdit: boolean;
  /** Called after the list changed. */
  onChanged?: (() => void) | undefined;
}

/**
 * "Work types this employee may be paid for" on the employee's Payroll Details. By default an employee can be paid for ANY work type: the list is empty and nothing is limited.
 * Setting a list limits this employee to those work types: a pay-run line must then name one of them. Nothing ever fills the list in, past payrolls never change, and the list
 * is changed as a whole (the server checks every newly added work type). The server decides every permission; what is hidden here is only what is not offered.
 */
export function AllowedWorkTypesSection({ employeeId, serviceTypes, serviceTypesReady, canEdit, onChanged }: AllowedWorkTypesSectionProps) {
  const uid = useId();
  const [data, setData] = useState<AllowedWorkTypes | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .getAllowedWorkTypes(employeeId, controller.signal)
      .then((result) => { if (!controller.signal.aborted) { setData(result); setLoadError(null); } })
      .catch((err: unknown) => { if (!controller.signal.aborted) setLoadError(healthcareErrorMessage(err, 'Could not load the work types this employee may be paid for.')); });
    return () => controller.abort();
  }, [employeeId, nonce]);

  // Every active work type, plus the ones already on the list (a work type that was deactivated since stays on the list until it is unticked).
  const options = useMemo(() => {
    const activeIds = new Set(serviceTypes.filter((t) => t.isActive).map((t) => t.id));
    const rows = serviceTypes.filter((t) => t.isActive).map((t) => ({ id: t.id, name: t.name, code: t.code, inactive: false }));
    for (const item of data?.items ?? []) {
      if (!activeIds.has(item.serviceTypeId)) rows.push({ id: item.serviceTypeId, name: item.name, code: item.code, inactive: true });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [serviceTypes, data]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q === '' ? options : options.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q));
  }, [options, filter]);

  const currentIds = useMemo(() => new Set((data?.items ?? []).map((i) => i.serviceTypeId)), [data]);
  const unchanged = selected.size === currentIds.size && [...selected].every((id) => currentIds.has(id));
  const tooMany = selected.size > MAX_ALLOWED_WORK_TYPES;

  function openEditor() {
    setSelected(new Set(currentIds));
    setFilter('');
    setSaveError(null);
    setStatus(null);
    setConfirmingRemove(false);
    setEditing(true);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSaveError(null);
  }

  async function save(ids: readonly string[], done: string) {
    setBusy(true);
    setSaveError(null);
    try {
      const result = await healthcareRatesGateway.setAllowedWorkTypes(employeeId, ids);
      setData(result);
      setEditing(false);
      setConfirmingRemove(false);
      setStatus(done);
      onChanged?.();
    } catch (err) {
      // Nothing was saved: the choices stay as they were so they can be corrected.
      setSaveError(healthcareErrorMessage(err, 'Could not save the work types.'));
    } finally {
      setBusy(false);
    }
  }

  const editable = canEdit && serviceTypesReady;
  const items = data?.items ?? [];

  return (
    <div data-testid="allowed-work-types">
      <h5 id={`${uid}-title`}>Work types this employee may be paid for</h5>
      <p className="hdc-note">
        By default an employee can be paid for any work type. Set a list to limit this employee to specific work types: a pay-run line must then name one of them. Nothing is limited
        unless you set a list, and payrolls that are already approved never change.
      </p>
      {status && <p className="jr-ok" role="status">{status}</p>}

      {loadError ? (
        <div role="alert">
          <p className="jr-error">{loadError}</p>
          <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={reload}>Retry</button>
        </div>
      ) : data === null ? (
        <p className="muted" role="status">Loading work types…</p>
      ) : (
        <>
          {!data.isRestricted ? (
            <p data-testid="work-types-unrestricted">No limit is set. This employee can be paid for any work type.</p>
          ) : (
            <div className="jr-table-scroll" role="region" aria-label="Allowed work types (scrolls sideways on a narrow screen)" tabIndex={0}>
              <table className="jr-table" aria-label="Allowed work types">
                <thead><tr><th scope="col">Work type</th><th scope="col">Code</th></tr></thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.serviceTypeId}><th scope="row">{i.name}</th><td>{i.code}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.isRestricted && <p className="hdc-note" data-testid="work-types-restricted-note">A pay-run line for this employee must name one of these work types.</p>}

          {!canEdit && <p className="jr-readonly" role="note">You can see this list but not change it.</p>}
          {canEdit && !serviceTypesReady && (
            <p className="jr-readonly" role="note">The work types could not be loaded, so this list cannot be changed here right now.</p>
          )}

          {editable && !editing && !confirmingRemove && (
            <p className="hdc-actions-row">
              <button type="button" className="jr-btn jr-btn--secondary" onClick={openEditor}>{data.isRestricted ? 'Change the list' : 'Limit work types'}</button>
              {data.isRestricted && (
                <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { setConfirmingRemove(true); setStatus(null); setSaveError(null); }}>Remove the limit</button>
              )}
            </p>
          )}

          {editable && confirmingRemove && (
            <div role="group" aria-label="Remove the limit" className="hdc-form">
              <p>Remove the limit? This employee could then be paid for any work type.</p>
              {saveError && <p className="jr-error jr-error--block" role="alert">{saveError}</p>}
              <div className="jr-actions hdc-form__actions">
                <button type="button" className="jr-btn jr-btn--primary" disabled={busy} onClick={() => void save([], 'The limit was removed. This employee can be paid for any work type.')}>
                  {busy ? 'Saving…' : 'Confirm remove the limit'}
                </button>
                <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={() => { setConfirmingRemove(false); setSaveError(null); }}>Cancel</button>
              </div>
            </div>
          )}

          {editable && editing && (
            <form
              className="hdc-form" aria-label="Choose the work types this employee may be paid for" noValidate
              onSubmit={(e) => { e.preventDefault(); if (!busy && !unchanged && selected.size > 0 && !tooMany) void save([...selected], 'Work types saved.'); }}
            >
              <div className="jr-field">
                <label htmlFor={`${uid}-filter`}>Search work types</label>
                <input id={`${uid}-filter`} type="search" value={filter} autoComplete="off" onChange={(e) => setFilter(e.target.value)} />
              </div>
              <fieldset className="hdc-checklist" aria-describedby={`${uid}-count`}>
                <legend>Work types</legend>
                {options.length === 0 && <p className="muted">There are no active work types yet. Add one under Settings, Payroll, Compensation Rates.</p>}
                {options.length > 0 && visible.length === 0 && <p className="muted" role="status">No work type matches that search.</p>}
                {visible.map((o) => (
                  <label key={o.id} className="hdc-check">
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
                    <span>{o.name} <span className="muted">{o.code}</span>{o.inactive ? <span className="muted"> (no longer active; it stays until you untick it)</span> : null}</span>
                  </label>
                ))}
              </fieldset>
              <p id={`${uid}-count`} className="jr-hint" aria-live="polite">
                {selected.size} selected (up to {MAX_ALLOWED_WORK_TYPES}).
                {selected.size === 0 ? ' Choose at least one work type, or cancel and use Remove the limit.' : ''}
                {tooMany ? ` Choose no more than ${MAX_ALLOWED_WORK_TYPES}.` : ''}
              </p>
              {saveError && <p className="jr-error jr-error--block" role="alert">{saveError}</p>}
              <div className="jr-actions hdc-form__actions">
                <button type="submit" className="jr-btn jr-btn--primary" disabled={busy || unchanged || selected.size === 0 || tooMany}>{busy ? 'Saving…' : 'Save work types'}</button>
                <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={() => { setEditing(false); setSaveError(null); }}>Cancel</button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
